import type { AssetContainer } from "@babylonjs/core/assetContainer";
import { LoadAssetContainerAsync } from "@babylonjs/core/Loading/sceneLoader";
import type { Scene } from "@babylonjs/core/scene";
import type { VisualContentEntry, VisualContentMetrics } from "../config/catalog-schema";
import { evaluateContentBudget } from "./catalog-validation";

export interface CachedVisualAsset {
  readonly container: AssetContainer;
  readonly metrics: VisualContentMetrics;
}

export class VisualAssetCache {
  private readonly cache = new Map<string, Promise<CachedVisualAsset>>();

  constructor(private readonly maxEntries = 4) {}

  get(entry: VisualContentEntry, scene: Scene): Promise<CachedVisualAsset> {
    const key = `${entry.id}@${entry.version}`;
    const existing = this.cache.get(key);
    if (existing) {
      this.cache.delete(key);
      this.cache.set(key, existing);
      return existing;
    }
    this.evictOldestIfNeeded();
    const pending = this.load(entry, scene).catch((error: unknown) => {
      this.cache.delete(key);
      throw error;
    });
    this.cache.set(key, pending);
    return pending;
  }

  clear(): void {
    this.cache.forEach((pending) => void pending.then((asset) => asset.container.dispose()).catch(() => undefined));
    this.cache.clear();
  }

  private async load(entry: VisualContentEntry, scene: Scene): Promise<CachedVisualAsset> {
    if (!entry.modelUrl) throw new Error(`${entry.id} has no GLB URL`);
    const response = await fetch(entry.modelUrl, { cache: "force-cache" });
    if (!response.ok) throw new Error(`${entry.id} GLB request failed with ${response.status}`);
    const bytes = await response.arrayBuffer();
    const byteBudget = evaluateContentBudget(
      { ...entry.declaredMetrics, compressedBytes: bytes.byteLength },
      entry.budget,
    );
    if (!byteBudget.accepted) throw new Error(`${entry.id} rejected before decoding: ${byteBudget.violations.join(", ")}`);

    await import("@babylonjs/loaders/glTF");
    const container = await LoadAssetContainerAsync(new Uint8Array(bytes), scene, {
      pluginExtension: ".glb",
      name: `${entry.id}.glb`,
    });
    const metrics = inspectContainer(container, bytes.byteLength);
    const evaluation = evaluateContentBudget(metrics, entry.budget);
    if (!evaluation.accepted) {
      container.dispose();
      throw new Error(`${entry.id} rejected after decoding: ${evaluation.violations.join(", ")}`);
    }
    return { container, metrics };
  }

  private evictOldestIfNeeded(): void {
    if (this.cache.size < this.maxEntries) return;
    const oldestKey = this.cache.keys().next().value as string | undefined;
    if (!oldestKey) return;
    const oldest = this.cache.get(oldestKey);
    this.cache.delete(oldestKey);
    if (oldest) void oldest.then((asset) => asset.container.dispose()).catch(() => undefined);
  }
}

function inspectContainer(container: AssetContainer, compressedBytes: number): VisualContentMetrics {
  const indices = container.meshes.reduce((sum, mesh) => sum + mesh.getTotalIndices(), 0);
  const maxTextureSize = container.textures.reduce((largest, texture) => {
    const size = texture.getSize();
    return Math.max(largest, size.width, size.height);
  }, 0);
  return {
    compressedBytes,
    triangles: Math.ceil(indices / 3),
    materials: container.materials.length,
    maxTextureSize,
    skeletalAnimations: container.skeletons.length,
  };
}
